import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import ActionProposal from "./ActionProposal";
import ChatMessage from "./ChatMessage";

/**
 * チャットパネル本体
 * メッセージ一覧 + 入力欄 + ローディング表示
 */
const ChatPanel = ({
  messages,
  pendingActions,
  isLoading,
  onSendMessage,
  onApplyActions,
  onCancelActions,
  onReset,
  assetNames,
}) => {
  const [inputText, setInputText] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // 新メッセージ追加時に最下部へスクロール
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    const text = inputText.trim();
    if (!text || isLoading) return;
    onSendMessage(text);
    setInputText("");
  };

  const handleKeyDown = (e) => {
    // 日本語IME変換中のEnterキーはメッセージ送信しない
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <PanelContainer>
      {/* ヘッダー */}
      <PanelHeader>
        <HeaderTitle>
          <AIIcon>🤖</AIIcon>
          <span>AIアシスタント</span>
        </HeaderTitle>
        <HeaderActions>
          <ResetButton onClick={onReset} title="チャット履歴をリセット">
            🔄
          </ResetButton>
        </HeaderActions>
      </PanelHeader>

      {/* メッセージエリア */}
      <MessagesArea>
        {messages.map((message, index) => (
          <React.Fragment key={index}>
            <ChatMessage message={message} />
          </React.Fragment>
        ))}

        {/* ローディング表示 */}
        {isLoading && (
          <LoadingRow>
            <LoadingBubble>
              <Dot delay="0s" />
              <Dot delay="0.2s" />
              <Dot delay="0.4s" />
            </LoadingBubble>
          </LoadingRow>
        )}

        <div ref={messagesEndRef} />
      </MessagesArea>

      {/* AI提案確認UI（常にスクロールエリア外・入力欄の上に固定表示） */}
      {pendingActions.length > 0 && (
        <ActionProposal
          actions={pendingActions}
          onApply={onApplyActions}
          onCancel={onCancelActions}
          assetNames={assetNames}
        />
      )}

      {/* 入力エリア */}
      <InputArea>
        <TextInput
          ref={inputRef}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="メッセージを入力... (Enter で送信)"
          disabled={isLoading}
          rows={2}
        />
        <SendButton
          onClick={handleSend}
          disabled={isLoading || !inputText.trim()}
        >
          {isLoading ? "..." : "送信"}
        </SendButton>
      </InputArea>

      <HintText>Shift+Enter で改行</HintText>
    </PanelContainer>
  );
};

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #ffffff;
  border-radius: 12px 12px 0 0;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background-color: #192e55;
  border-radius: 12px 12px 0 0;
  flex-shrink: 0;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 13px;
  font-weight: bold;
`;

const AIIcon = styled.span`
  font-size: 16px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 6px;
`;

const ResetButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
    color: white;
  }
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  background-color: #fafbfc;
`;

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`;

const LoadingBubble = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: #f0f7fc;
  padding: 10px 16px;
  border-radius: 4px 16px 16px 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
`;

const Dot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #2397ce;
  animation: bounce 1.2s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay};

  @keyframes bounce {
    0%, 80%, 100% {
      transform: scale(0.6);
      opacity: 0.4;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const InputArea = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 12px 6px;
  border-top: 1px solid #e5edf1;
  background-color: #ffffff;
  flex-shrink: 0;
`;

const TextInput = styled.textarea`
  flex: 1;
  padding: 8px 12px;
  border: 1.5px solid #c0d8e8;
  border-radius: 8px;
  font-size: 12px;
  font-family: inherit;
  resize: none;
  outline: none;
  line-height: 1.5;
  color: #192e55;
  background-color: ${({ disabled }) => (disabled ? "#f5f5f5" : "#ffffff")};

  &:focus {
    border-color: #2397ce;
    box-shadow: 0 0 0 2px rgba(35, 151, 206, 0.15);
  }

  &::placeholder {
    color: #90a4ae;
  }
`;

const SendButton = styled.button`
  padding: 8px 14px;
  background-color: ${({ disabled }) => (disabled ? "#b0c8d8" : "#2397ce")};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: bold;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  white-space: nowrap;
  height: 36px;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background-color: #1a7aaa;
  }

  &:active:not(:disabled) {
    background-color: #192e55;
  }
`;

const HintText = styled.div`
  font-size: 9px;
  color: #90a4ae;
  text-align: right;
  padding: 0 14px 6px;
  background-color: #ffffff;
`;

export default ChatPanel;
